import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';

import { type ScreenBreakpoint, setScreenBreakpoint } from '../Redux/ApplicationActions';
import type { RootState } from '../Redux';

// When modifying values, refer to App.css breakpoints
const breakpoints = {
  tablet: 768,
  laptop: 992,
  desktop: 1280,
} as const;

function getScreenWidth(): ScreenBreakpoint {
  const width = window.innerWidth;
  if (width < breakpoints.tablet) return 'mobile';
  if (width < breakpoints.laptop) return 'tablet';
  if (width < breakpoints.desktop) return 'laptop';
  return 'desktop';
}

/**
 * Returns the current screen break point from the Redux store.
 */
export function useScreenBreakpoint(): ScreenBreakpoint {
  return useSelector((state: RootState) => state.application.screenBreakpoint);
}

/**
 * Tracks screen width category in Redux. Should be called once near the top of
 * the component tree (inside the Redux Provider). Components that need this
 * value should read it from the store via useSelector.
 */
export function useTrackScreenBreakpoint() {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setScreenBreakpoint(getScreenWidth()));

    const handleChange = debounce(() => {
      dispatch(setScreenBreakpoint(getScreenWidth()));
    }, 200);

    window.addEventListener('resize', handleChange);

    return () => {
      window.removeEventListener('resize', handleChange);
      handleChange.cancel();
    };
  }, [dispatch]);
}
